import { authFetch } from '../../api/csrf';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  Clock,
  Plus,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Mail,
  User,
  X,
  Info,
  ChevronDown,
  Search,
  MoreHorizontal,
  Loader2,
  FileText,
  Trash2,
  Check,
  Upload
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import Button from '../../components/common/Button/Button';
import essService from '../../api/services/essService';
import { formatDate } from '../../utils/stringUtils';
import LeaveRequestForm from '../../components/hrms/LeaveRequestForm';

const ApplyLeave = () => {
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([]);

  // Employee data
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);

  // Leave policies
  const [leavePolicies, setLeavePolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    leavePolicyId: '',
    startDate: '',
    endDate: '',
    reason: '',
    requestedDays: 0,
    documentUrl: '',
    documentPublicId: '',
    documentResourceType: '',
    isHalfDay: false,
    halfDayType: 'first_half',
  });

  const [isUploading, setIsUploading] = useState(false);

  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Sandwich Rule State
  const [showSandwichConfirm, setShowSandwichConfirm] = useState(false);
  const [sandwichValidationResult, setSandwichValidationResult] = useState(null);
  const [isValidatingSandwich, setIsValidatingSandwich] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Leave balance view state
  const [showAllBalances, setShowAllBalances] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Data for charts
  const [weeklyData, setWeeklyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [consumedLeaveData, setConsumedLeaveData] = useState([]);

  // Calculate chart data whenever leaveRequests changes
  useEffect(() => {
    if (leaveRequests.length > 0) {
      // Weekly stats
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const weekly = days.map(day => ({ name: day, value: 0 }));

      leaveRequests.forEach(req => {
        if (req.status === 'approved') {
          const date = new Date(req.startDate);
          const dayIndex = (date.getDay() + 6) % 7; // Convert 0-6 (Sun-Sat) to 0-6 (Mon-Sun)
          weekly[dayIndex].value += parseFloat(req.requestedDays || 0);
        }
      });
      setWeeklyData(weekly);

      // Monthly stats
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthly = months.map(month => ({ name: month, value: 0 }));

      leaveRequests.forEach(req => {
        if (req.status === 'approved') {
          const date = new Date(req.startDate);
          const monthIndex = date.getMonth();
          monthly[monthIndex].value += parseFloat(req.requestedDays || 0);
        }
      });
      setMonthlyData(monthly);
    }
  }, [leaveRequests]);

  // Calculate consumed leave types whenever employeeData changes
  useEffect(() => {
    if (employeeData && employeeData.leaveInfo && employeeData.leaveInfo.leaveTypes) {
      const data = employeeData.leaveInfo.leaveTypes.map(lt => ({
        name: lt.type,
        value: lt.used || 0
      })).filter(item => item.value > 0);
      setConsumedLeaveData(data);
    }
  }, [employeeData]);


  // Load employee profile on mount
  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true);
      try {
        await loadEmployeeProfile();
      } finally {
        setInitialLoading(false);
      }
    };
    loadInitialData();

    // Fetch leave types for filter dropdown
    const fetchLeaveTypeOptions = async () => {
      try {
        const response = await authFetch(`${process.env.REACT_APP_API_BASE_URL}/config/leave-types/dropdown`, {
        credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setLeaveTypeOptions(result.data);
          }
        }
      } catch (error) {

      }
    };
    fetchLeaveTypeOptions();
  }, []);

  // Load leave policies when employee data is available
  useEffect(() => {
    if (employeeData) {
      loadLeavePolicies(employeeData);
    }
  }, [employeeData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (employeeId && employeeData) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterType]);

  // Load leave requests when employee data is available
  useEffect(() => {
    if (employeeId && employeeData) {
      loadLeaveRequests();
    } else {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, employeeData, leavePolicies, currentPage, filterStatus, filterType, itemsPerPage]);

  // Search and filter requests client-side for the search query
  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const filtered = leaveRequests.filter(req =>
        (req.leaveType && req.leaveType.toLowerCase().includes(query)) ||
        (req.reason && req.reason.toLowerCase().includes(query)) ||
        (req.status && req.status.toLowerCase().includes(query))
      );
      setFilteredRequests(filtered);
    } else {
      setFilteredRequests(leaveRequests);
    }
  }, [leaveRequests, searchQuery]);

  const loadEmployeeProfile = async () => {
    try {
      const result = await essService.getProfile();
      if (result.success && result.data) {
        setEmployeeId(result.data._id || result.data.id);

        // Fetch leave balances separately (New Architecture)
        const balanceResult = await essService.getLeaveBalances();
        if (balanceResult.success && balanceResult.data) {
          const updatedEmployeeData = {
            ...result.data,
            leaveInfo: balanceResult.data
          };
          setEmployeeData(updatedEmployeeData);
        } else {
          setEmployeeData(result.data);
        }
      }
    } catch (error) {
    }
  };

  const loadLeavePolicies = async (currentEmployeeData = null) => {
    try {
      setLoadingPolicies(true);
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/leave-policies/leave?isActive=true`, {
        credentials: 'include',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            }
        }
      );

      if (response.ok) {
        const result = await response.json();


        // Handle both response formats: {status: "success", data: [...]} or {responseCode: 200, data: [...]}
        let allPolicies = [];
        if (result.status === 'success' && result.data) {
          allPolicies = result.data || [];
        } else if (result.responseCode === 200 && result.data) {
          allPolicies = result.data || [];
        } else if (Array.isArray(result)) {
          allPolicies = result;
        } else if (Array.isArray(result.data)) {
          allPolicies = result.data;
        }



        // Use currentEmployeeData parameter or fall back to state
        const empData = currentEmployeeData || employeeData;

        // Keep broadly applicable policies visible and only apply gender checks
        // when the policy is explicitly gender-specific.
        if (empData && empData.personalInfo && empData.personalInfo.gender) {
          const employeeGender = empData.personalInfo.gender.toLowerCase();


          const filteredPolicies = allPolicies.filter(policy => {
            // Get applicableRoles - can be array or string
            const applicableRoles = policy.applicableRoles || [];
            const rolesArray = (Array.isArray(applicableRoles) ? applicableRoles : [applicableRoles])
              .map(role => role?.toString().trim().toLowerCase())
              .filter(Boolean);



            // If policy is for everyone, always show it.
            if (
              rolesArray.includes('all') ||
              rolesArray.includes('all employees')
            ) {

              return true;
            }

            const hasGenderRestriction = rolesArray.some(role =>
              ['male', 'males', 'female', 'females'].includes(role)
            );

            // If the policy is not gender-restricted, don't hide it here.
            if (!hasGenderRestriction) {
              return true;
            }

            if (employeeGender === 'male') {
              return rolesArray.includes('male') || rolesArray.includes('males');
            }

            if (employeeGender === 'female') {
              return rolesArray.includes('female') || rolesArray.includes('females');
            }

            return true;
          });


          setLeavePolicies(filteredPolicies);
        } else {
          // If employee gender is not available, show all policies

          setLeavePolicies(allPolicies);
        }
      }
    } catch (error) {

    } finally {
      setLoadingPolicies(false);
    }
  };

  const loadLeaveRequests = async () => {
    if (!employeeId || !employeeData) {
      return;
    }

    try {
      setLoading(true);

      // Pass pagination and filters to the service
      const result = await essService.getLeaveHistory(
        currentPage,
        itemsPerPage,
        filterStatus,
        filterType
      );

      if (result.success && result.data) {
        const requests = result.data.requests || [];

        // Transform requests to include balance (same logic as HRMS)
        const transformedRequests = requests.map(request => {
          // Find matching leave type in employee's leaveInfo
          const leaveTypes = employeeData?.leaveInfo?.leaveTypes || [];
          const matchingLeaveType = leaveTypes.find(lt => lt.type === request.leaveType);

          // Get policy entitlement if available (same as HRMS)
          let policyEntitlement = 0;
          if (request.leavePolicyId && leavePolicies.length > 0) {
            const policy = leavePolicies.find(p =>
              p._id === request.leavePolicyId || p.id === request.leavePolicyId
            );
            if (policy) {
              policyEntitlement = policy.entitlement || 0;
            }
          }

          // Calculate balance
          const total = matchingLeaveType?.total || policyEntitlement || 0;
          const used = matchingLeaveType?.used || 0;
          const remaining = matchingLeaveType?.remaining !== undefined
            ? matchingLeaveType.remaining
            : (total > 0 ? total - used : 0);

          return {
            ...request,
            leaveBalance: {
              total,
              used,
              remaining
            }
          };
        });

        setLeaveRequests(transformedRequests);
        const pagination = result.data.pagination || {};
        setTotalPages(pagination.totalPages || 1);
        setTotalItems(pagination.totalRequests || transformedRequests.length);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilterStatus('all');
    setFilterType('all');
  };

  const calculateDays = useCallback((startDate, endDate, isHalfDay = false) => {
    if (!startDate || !endDate) return 0;
    if (isHalfDay) return 0.5;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  }, []);

  useEffect(() => {
    if (formData.startDate && (formData.isHalfDay || formData.endDate)) {
      const actualEndDate = formData.isHalfDay ? formData.startDate : formData.endDate;
      const days = calculateDays(formData.startDate, actualEndDate, formData.isHalfDay);
      setFormData(prev => ({ 
        ...prev, 
        requestedDays: days,
        ...(formData.isHalfDay ? { endDate: formData.startDate } : {})
      }));
    } else {
      setFormData(prev => ({ ...prev, requestedDays: 0 }));
    }
  }, [formData.startDate, formData.endDate, formData.isHalfDay, calculateDays]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      // Cross-validation: if startDate changes, re-validate endDate
      if (name === 'startDate' && newData.endDate && touched.endDate) {
        const endDateError = validateSingleField('endDate', newData.endDate, newData);
        setFormErrors(prevErrors => ({ ...prevErrors, endDate: endDateError }));
      }

      return newData;
    });

    // Field-level validation on change
    if (touched[name]) {
      const error = validateSingleField(name, value);
      setFormErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('folder', 'leave-documents');

    try {
      const response = await authFetch(`${process.env.REACT_APP_API_BASE_URL}/upload/cloudinary`, {
        credentials: 'include',
        method: 'POST',
        
        body: uploadData
      });

      if (!response.ok) throw new Error('Failed to upload document');
      const result = await response.json();

      if (result.success && result.data && result.data.url) {
        setFormData(prev => ({ 
          ...prev, 
          documentUrl: result.data.url,
          documentPublicId: result.data.public_id,
          documentResourceType: result.data.resource_type || 'raw'
        }));
        setFormErrors(prev => ({ ...prev, documentUrl: '' })); // clear error
      }
    } catch (error) {
      setFormErrors(prev => ({ ...prev, documentUrl: 'Upload failed. Try again.' }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = async () => {
    if (!formData.documentPublicId) {
      setFormData(prev => ({ 
        ...prev, 
        documentUrl: '',
        documentPublicId: '',
        documentResourceType: ''
      }));
      return;
    }

    try {
      setIsUploading(true);
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/upload/cloudinary?public_id=${formData.documentPublicId}&resource_type=${formData.documentResourceType}`, {
        credentials: 'include',
          method: 'DELETE',
          headers: {
            }
        }
      );

      if (response.ok) {
        setFormData(prev => ({ 
          ...prev, 
          documentUrl: '',
          documentPublicId: '',
          documentResourceType: ''
        }));
      } else {
        throw new Error('Failed to delete file');
      }
    } catch (error) {
      // Even if delete fails on S3, we clear it from form for user experience
      setFormData(prev => ({ 
        ...prev, 
        documentUrl: '',
        documentPublicId: '',
        documentResourceType: ''
      }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateSingleField(name, value);
    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateSingleField = (name, value, customData = null) => {
    const data = customData || formData;
    switch (name) {
      case 'leavePolicyId':
        return !value ? 'Please select a leave policy' : '';
      case 'startDate': {
        if (!value) return 'Start date is required';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startDate = new Date(value);
        startDate.setHours(0, 0, 0, 0);
        if (startDate < today) return 'Start date cannot be in the past';
        return '';
      }
      case 'endDate': {
        if (!value) return 'End date is required';
        if (data.startDate) {
          const startDate = new Date(data.startDate);
          const endDate = new Date(value);
          if (endDate < startDate) return 'End date must be on or after start date';
        }
        return '';
      }
      case 'reason':
        return !value || value.trim() === '' ? 'Reason is required' : '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const errors = {};
    const fields = ['leavePolicyId', 'startDate', 'endDate', 'reason'];

    fields.forEach(field => {
      const error = validateSingleField(field, formData[field]);
      if (error) errors[field] = error;
    });

    setFormErrors(errors);
    // Mark all fields as touched on submit
    const allTouched = {};
    fields.forEach(field => { allTouched[field] = true; });
    setTouched(allTouched);

    return Object.keys(errors).length === 0;
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (loadingPolicies || leavePolicies.length === 0) {
      alert('Please wait for leave policies to load before submitting.');
      return;
    }

    const selectedPolicy = leavePolicies.find(p =>
      p._id === formData.leavePolicyId || p.id === formData.leavePolicyId
    );

    if (!selectedPolicy) {
      alert('Please select a valid leave policy.');
      return;
    }

    // Call Sandwich Validation API
    try {
      setIsValidatingSandwich(true);
      const validateResponse = await authFetch(`${process.env.REACT_APP_API_BASE_URL}/leave-management/validate`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          },
        body: JSON.stringify({
          leaveType: selectedPolicy?.type,
          startDate: formData.startDate,
          endDate: formData.endDate,
          employeeId: employeeId,
          leavePolicyId: formData.leavePolicyId,
          isHalfDay: false,
          reason: formData.reason
        })
      });

      const validateResult = await validateResponse.json();

      if (validateResult.success) {
        if (!validateResult.data.isValid) {
          alert(validateResult.data.error || "Validation failed");
          return;
        }

        if (validateResult.data.sandwichDays > 0) {
          setSandwichValidationResult(validateResult.data);
          setShowSandwichConfirm(true);
        } else {
          await processSubmit();
        }
      } else {
        throw new Error(validateResult.message || "Validation failed");
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setIsValidatingSandwich(false);
    }
  };

  const processSubmit = async () => {
    setLoading(true);
    try {
      const requestedDays = formData.requestedDays || calculateDays(formData.startDate, formData.endDate);

      const selectedPolicy = leavePolicies.find(p =>
        p._id === formData.leavePolicyId || p.id === formData.leavePolicyId
      );

      const leaveRequestPayload = {
        employeeId: employeeId,
        employeeName: `${employeeData?.personalInfo?.firstName || ''} ${employeeData?.personalInfo?.lastName || ''}`.trim(),
        employeeEmail: employeeData?.contactInfo?.personalEmail || '',
        leavePolicyId: formData.leavePolicyId,
        leaveTypeId: selectedPolicy.leaveTypeId,
        leaveType: selectedPolicy.type,
        startDate: formData.startDate,
        endDate: formData.endDate,
        totalDays: requestedDays,
        requestedDays: requestedDays,
        isHalfDay: formData.isHalfDay,
        halfDayType: formData.isHalfDay ? formData.halfDayType : null,
        reason: formData.reason || '',
        status: 'pending',
        isActive: true,
        documentUrl: formData.documentUrl || '',
      };

      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/leave-management`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            },
          body: JSON.stringify(leaveRequestPayload)
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to create leave request';
        try {
          const errorResult = await response.json();
          errorMessage = errorResult.responseMessage || errorResult.message || errorResult.error || errorMessage;
        } catch (parseError) {
          errorMessage = `HTTP error! status: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      alert('Leave request submitted successfully!');
      setShowRequestForm(false);
      
      // Reset form
      setFormData({
        leavePolicyId: '',
        startDate: '',
        endDate: '',
        reason: '',
        requestedDays: 0,
        documentUrl: '',
      });
      setFormErrors({});
      setTouched({});

      // Reload balances first so leave-request rows get the updated remaining days
      await loadEmployeeProfile();
      await loadLeaveRequests();

    } catch (error) {
      alert(error.message || 'Failed to submit leave request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (request) => {
    // Use the ESS leave balance already loaded in state (includes pendingDays)
    const leaveTypes = employeeData?.leaveInfo?.leaveTypes || [];
    const matchingLeaveType = leaveTypes.find(lt =>
      lt.type === request.leaveType ||
      (lt.leaveTypeId && request.leaveTypeId &&
        lt.leaveTypeId.toString() === request.leaveTypeId.toString())
    );

    let policyEntitlement = 0;
    if (request.leavePolicyId && leavePolicies.length > 0) {
      const policy = leavePolicies.find(p =>
        p._id === request.leavePolicyId || p.id === request.leavePolicyId
      );
      if (policy) policyEntitlement = policy.entitlement || 0;
    }

    const total = matchingLeaveType?.total || policyEntitlement || 0;
    const used = matchingLeaveType?.used || 0;
    const pending = matchingLeaveType?.pending || 0;
    const remaining = matchingLeaveType?.remaining !== undefined
      ? matchingLeaveType.remaining          // already = accrued - used - pending from ESS endpoint
      : Math.max(0, total - used - pending);

    setSelectedRequest({
      ...request,
      appliedDate: request.appliedDate || request.createdAt,
      approvedDate: request.approvedDate || request.approvedAt,
      leaveBalance: { total, used, pending, remaining }
    });
    setShowDetails(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };



  const formatDateForInput = (date) => {
    if (!date) return '';
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    if (typeof date === 'string') {
      return date.split('T')[0];
    }
    return date;
  };

  // Render detailed view
  const renderDetailsModal = () => {
    if (!showDetails || !selectedRequest) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white p-6 border-b border-gray-100 flex items-center justify-between z-10">
            <h2 className="text-xl font-bold text-gray-800">Leave Request Details</h2>
            <button
              onClick={() => setShowDetails(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {/* Employee Info */}
              <div className="space-y-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Employee Information</h3>
                <div className="flex items-start gap-3">
                  <div className="bg-purple-50 p-2 rounded-lg">
                    <User className="h-4 w-4 text-[#817af3]" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-700">
                      {selectedRequest.employeeName || (employeeData?.personalInfo?.firstName + ' ' + employeeData?.personalInfo?.lastName)}
                    </div>
                    <div className="text-xs text-gray-400">Employee</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-blue-50 p-2 rounded-lg">
                    <Mail className="h-4 w-4 text-blue-500" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-gray-700 truncate">
                      {selectedRequest.employeeEmail || employeeData?.contactInfo?.personalEmail || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-400">Email Address</div>
                  </div>
                </div>
              </div>

              {/* Leave Status */}
              <div className="space-y-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Status & Timeline</h3>
                <div className="flex items-start gap-3">
                  <div className="bg-orange-50 p-2 rounded-lg">
                    {getStatusIcon(selectedRequest.status)}
                  </div>
                  <div>
                    <div className={`text-sm font-bold capitalize ${selectedRequest.status === 'approved' ? 'text-green-500' :
                      selectedRequest.status === 'rejected' ? 'text-red-500' : 'text-yellow-500'
                      }`}>
                      {selectedRequest.status}
                    </div>
                    <div className="text-xs text-gray-400">Current Status</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-gray-50 p-2 rounded-lg">
                    <Clock className="h-4 w-4 text-gray-500" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-700">
                      {formatDate(selectedRequest.appliedDate, { includeTime: false })}
                    </div>
                    <div className="text-xs text-gray-400">Applied Date</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Leave Details Card */}
            <div className="bg-[#f8faff] rounded-2xl p-6 border border-gray-100 mb-8">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-xs text-gray-400 mb-1">Leave Type</div>
                  <div className="text-sm font-bold text-gray-700 capitalize">{selectedRequest.leaveType}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-1">Duration</div>
                  <div className="text-sm font-bold text-gray-700">{selectedRequest.requestedDays} Day(s)</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-1">Start Date</div>
                  <div className="text-sm font-bold text-gray-700">{formatDate(selectedRequest.startDate, { includeTime: false })}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-1">End Date</div>
                  <div className="text-sm font-bold text-gray-700">{formatDate(selectedRequest.endDate, { includeTime: false })}</div>
                </div>
              </div>
            </div>

            {/* Leave Balance Section */}
            {selectedRequest.leaveBalance && (
              <div className="mb-8">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Leave Balance (current)</h3>
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-white p-4 rounded-xl border border-gray-100 text-center">
                    <div className="text-lg font-bold text-blue-600">{selectedRequest.leaveBalance.total}</div>
                    <div className="text-[10px] text-gray-400 uppercase font-bold">Total</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-gray-100 text-center">
                    <div className="text-lg font-bold text-red-600">{selectedRequest.leaveBalance.used}</div>
                    <div className="text-[10px] text-gray-400 uppercase font-bold">Used</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-gray-100 text-center">
                    <div className="text-lg font-bold text-amber-500">{selectedRequest.leaveBalance.pending || 0}</div>
                    <div className="text-[10px] text-gray-400 uppercase font-bold">Pending</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-gray-100 text-center">
                    <div className="text-lg font-bold text-green-600">{selectedRequest.leaveBalance.remaining}</div>
                    <div className="text-[10px] text-gray-400 uppercase font-bold">Remaining</div>
                  </div>
                </div>
              </div>
            )}

            {/* Reason */}
            <div className="space-y-3">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Reason for Leave</h3>
              <div className="bg-gray-50 p-4 rounded-xl text-sm text-gray-600 italic">
                "{selectedRequest.reason || 'No reason provided'}"
              </div>
            </div>

            {/* Document Proof */}
            {selectedRequest.documentUrl && (
              <div className="mt-8 space-y-3">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Attached Document Proof</h3>
                <a
                  href={selectedRequest.documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-[#817af3] bg-white hover:bg-[#817af3]/5 transition-colors shadow-sm"
                >
                  <FileText className="h-4 w-4" />
                  <span>View Document Proof</span>
                </a>
              </div>
            )}

            {/* Admin Feedback */}
            {['rejected', 'cancelled', 'approved'].includes(selectedRequest.status) && selectedRequest.comments && (
              <div className="mt-8 pt-8 border-t border-gray-100 space-y-3">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Admin Feedback</h3>
                <div className="bg-purple-50 p-4 rounded-xl text-sm text-[#817af3] font-medium">
                  {selectedRequest.comments}
                </div>
                {selectedRequest.approvedDate && (
                  <div className="text-[10px] text-gray-400 text-right italic">
                    Action taken on {formatDate(selectedRequest.approvedDate, { includeTime: true })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end">
            <Button
              variant="outline"
              onClick={() => setShowDetails(false)}
              className="px-8"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  };



  if (initialLoading) {
    return (
      <div className="min-h-screen bg-[#f8faff] p-4 sm:p-6 flex items-center justify-center font-sans">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#817af3] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading leave data...</p>
        </div>
      </div>
    );
  }

  const COLORS = ['#817af3', '#fb7185', '#34d399', '#fbbf24', '#60a5fa', '#a78bfa'];

  const renderStatsCharts = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
      {/* Weekly Pattern */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-sm font-semibold text-gray-700">Weekly Pattern</h3>
          <Info className="h-4 w-4 text-gray-400" />
        </div>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={weeklyData}>
              <Bar dataKey="value" fill="#9c8ef1" radius={[4, 4, 0, 0]} barSize={24} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
              />
              <Tooltip
                cursor={{ fill: 'transparent' }}
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Consumed Leave Types */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-sm font-semibold text-gray-700">Consumed Leave Types</h3>
          <Info className="h-4 w-4 text-gray-400" />
        </div>
        <div className="h-48 relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={consumedLeaveData.length > 0 ? consumedLeaveData : [{ name: 'None', value: 1 }]}
                innerRadius={50}
                outerRadius={70}
                paddingAngle={5}
                dataKey="value"
              >
                {consumedLeaveData.length > 0 ? (
                  consumedLeaveData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))
                ) : (
                  <Cell fill="#f1f5f9" />
                )}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-[10px] font-bold text-gray-500 uppercase">Leave</span>
            <span className="text-[10px] font-bold text-gray-500 uppercase">Types</span>
          </div>
        </div>
      </div>

      {/* Monthly Stats */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-sm font-semibold text-gray-700">Monthly Stats</h3>
          <Info className="h-4 w-4 text-gray-400" />
        </div>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData}>
              <Bar dataKey="value" fill="#9c8ef1" radius={[4, 4, 0, 0]} barSize={16} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
              />
              <Tooltip
                cursor={{ fill: 'transparent' }}
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  const renderNewLeaveBalance = () => {
    if (!employeeData) return null;

    const leaveInfo = employeeData.leaveInfo || {};
    const leaveTypes = leaveInfo.leaveTypes || [];
    const allLeaveTypes = new Map();

    // Current month for accrual calculation (1-indexed)
    const currentMonth = new Date().getMonth() + 1;

    leaveTypes.forEach(lt => {
      allLeaveTypes.set(lt.type, {
        type: lt.type,
        displayName: lt.type, // Backend can provide displayName if needed, but for now using type
        total: lt.total || 0,
        used: lt.used || 0,
        accrued: lt.accrued || 0,
        pending: lt.pending || 0,
        remaining: lt.remaining || 0,
        annualQuota: lt.total || 0
      });
    });

    const balanceArray = Array.from(allLeaveTypes.values());

    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-700 mb-6">Leave Balances</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {balanceArray.map((balance, idx) => {
            const data = [
              { name: 'Available', value: Math.max(0, balance.remaining) },
              { name: 'Consumed', value: Math.max(0, balance.used) }
            ];
            const pieColor = COLORS[idx % COLORS.length];

            return (
              <div key={balance.type} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-5">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-sm font-bold text-gray-700 capitalize">{balance.displayName || balance.type}</h4>
                  </div>

                  <div className="h-32 relative mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data}
                          innerRadius={35}
                          outerRadius={50}
                          startAngle={90}
                          endAngle={450}
                          dataKey="value"
                        >
                          <Cell fill={pieColor} />
                          <Cell fill={pieColor + '44'} /> {/* 44 is approx 25% opacity for lighter shade */}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-sm font-bold text-gray-800">{balance.remaining} Days</span>
                      <span className="text-[10px] text-gray-500 font-medium">Available</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4">
                    <div>
                      <p className="text-[10px] uppercase font-bold text-gray-400 mb-1">Available</p>
                      <p className="text-sm font-bold text-gray-700">{balance.remaining} days</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase font-bold text-gray-400 mb-1">Consumed</p>
                      <p className="text-sm font-bold text-gray-700">{balance.used} days</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <p className="text-[10px] uppercase font-bold text-gray-400 mb-1">ACCRUED SO FAR</p>
                      <p className="text-sm font-bold text-gray-700">{balance.accrued || 0} days</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase font-bold text-gray-400 mb-1">ANNUAL QUOTA</p>
                      <p className="text-sm font-bold text-gray-700">{balance.annualQuota || 0} days</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8faff] p-4 sm:p-8 font-sans text-gray-700">
      <div className="max-w-[1400px] mx-auto">
        {/* Summary Header */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-800">Summary</h1>
        </div>

        {/* Pending Leave Requests Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Pending leave requests</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              {(() => {
                const pendingRequests = leaveRequests.filter(req => req.status === 'pending');
                if (pendingRequests.length === 0) {
                  return (
                    <div className="flex items-center">
                      <div className="bg-purple-50 p-4 rounded-xl mr-6">
                        <Calendar className="h-8 w-8 text-[#817af3]" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">Hurray! No pending leave requests</h3>
                        <p className="text-sm text-gray-500 mt-1">Request leave on the right!</p>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-[#817af3] uppercase tracking-wider">You have {pendingRequests.length} pending request(s)</h3>
                    <div className="flex flex-wrap gap-4">
                      {pendingRequests.slice(0, 3).map(req => (
                        <div key={req._id || req.id} className="flex-1 min-w-[200px] bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-center justify-between">
                          <div>
                            <div className="text-sm font-bold text-gray-700 capitalize">{req.leaveType}</div>
                            <div className="text-[10px] text-gray-400">{formatDate(req.startDate, { includeTime: false })} ({req.requestedDays} days)</div>
                          </div>
                          <button
                            onClick={() => handleViewDetails(req)}
                            className="p-2 hover:bg-white rounded-lg transition-colors text-[#817af3]"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {pendingRequests.length > 3 && (
                        <div className="flex items-center text-xs text-gray-400 italic">
                          + {pendingRequests.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-center space-y-3">
              <Button
                variant="primary"
                onClick={() => setShowRequestForm(true)}
                className="w-full bg-[#817af3] hover:bg-[#6b64d1] text-white py-2.5 rounded-lg font-semibold shadow-md shadow-purple-100"
              >
                Request Leave
              </Button>
            </div>
          </div>
        </div>

        {/* My Leave Stats Section */}
        <h2 className="text-lg font-semibold text-gray-700 mb-6">My Leave Stats</h2>
        {renderStatsCharts()}

        {/* Leave Balances Section */}
        {renderNewLeaveBalance()}

        {/* Other Leave Types Section */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 mb-8 flex items-center">
          <span className="text-sm font-bold text-gray-700 mr-4">Other Leave Types Available:</span>
          <span className="text-sm text-gray-600">Restricted Holiday leave</span>
        </div>

        {/* Leave History Section */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-6">Leave History</h2>

          {/* Table Filters */}
          <div className="bg-white p-4 rounded-t-xl border-x border-t border-gray-100 flex flex-wrap items-center gap-4">
            <div className="relative min-w-[200px]">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full pl-3 pr-10 py-2.5 bg-[#f8faff] border border-gray-100 rounded-lg text-sm text-gray-600 appearance-none focus:outline-none focus:ring-2 focus:ring-[#817af3]/20"
              >
                <option value="all">Leave Type</option>
                {leaveTypeOptions.map((type) => (
                  <option key={type._id} value={type.name}>{type.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            <div className="relative min-w-[200px]">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pl-3 pr-10 py-2.5 bg-[#f8faff] border border-gray-100 rounded-lg text-sm text-gray-600 appearance-none focus:outline-none focus:ring-2 focus:ring-[#817af3]/20"
              >
                <option value="all">Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-[#f8faff] border border-gray-100 rounded-lg text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#817af3]/20"
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border-x border-b border-gray-100 rounded-b-xl overflow-hidden shadow-sm">
            <div className="p-3 bg-white border-b border-gray-50 flex justify-end">
              <span className="text-[10px] font-bold text-gray-400 uppercase">Total: {totalItems}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#f8faff] border-y border-gray-50">
                    <th className="px-6 py-4 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Leave Dates</th>
                    <th className="px-6 py-4 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Leave Type</th>
                    <th className="px-6 py-4 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Requested By</th>
                    <th className="px-6 py-4 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Action Taken On</th>
                    <th className="px-6 py-4 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Leave Note</th>
                    <th className="px-6 py-4 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Reject/Cancellation Reason</th>
                    <th className="px-6 py-4 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-400 text-sm">Loading leave history...</td>
                    </tr>
                  ) : filteredRequests.length > 0 ? (
                    filteredRequests.map((request) => (
                      <tr key={request._id} className="hover:bg-[#f8faff]/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-gray-700">{formatDate(request.startDate, { includeTime: false })}</div>
                          <div className="text-[10px] text-gray-400">{request.requestedDays} Day</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-gray-700 capitalize">{request.leaveType}</div>
                          <div className="text-[10px] text-gray-400">Requested on {formatDate(request.createdAt, { includeTime: false })}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`inline-flex flex-col`}>
                            <span className={`text-[10px] font-bold capitalize ${request.status === 'approved' ? 'text-green-500' :
                              request.status === 'rejected' ? 'text-red-500' : 'text-yellow-500'
                              }`}>
                              {request.status}
                            </span>
                            <span className="text-[10px] text-gray-400">by Attendance Penalisation Policy</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          Penalisation Policy
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {request.status !== 'pending' ? formatDate(request.approvedAt || request.updatedAt, { includeTime: true }) : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px] truncate">
                          {request.reason || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {request.rejectionReason || request.cancellationReason || (['rejected', 'cancelled'].includes(request.status) ? request.comments : '') || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(request)}
                            className="text-[#817af3] hover:text-[#6b64d1] transition-colors p-1"
                            title="View Details"
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-400 text-sm italic">No data to display.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Table Pagination */}
            <div className="p-4 bg-white border-t border-gray-50 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-[10px] font-bold text-gray-400 uppercase">
                1 to {filteredRequests.length} of {totalItems}
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1 text-[10px] font-bold text-gray-600">
                  Page 1 of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-1 text-gray-300 hover:text-gray-500 disabled:opacity-50" disabled>
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <button className="p-1 text-gray-300 hover:text-gray-500 disabled:opacity-50" disabled>
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Details Modal */}
        {renderDetailsModal()}

        {/* New Request Form Modal */}
        {showRequestForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">New Leave Request</h3>
                <button
                  onClick={() => {
                    setShowRequestForm(false);
                    setFormData({
                      leavePolicyId: '',
                      startDate: '',
                      endDate: '',
                      reason: '',
                      requestedDays: 0,
                      documentUrl: '',
                    });
                    setFormErrors({});
                    setTouched({});
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <LeaveRequestForm
                isESS={true}
                formData={formData}
                setFormData={setFormData}
                formErrors={formErrors}
                touched={touched}
                handleInputChange={handleInputChange}
                handleBlur={handleBlur}
                handleSubmit={handleSubmitRequest}
                onCancel={() => {
                  setShowRequestForm(false);
                  setFormData({
                    leavePolicyId: '',
                    startDate: '',
                    endDate: '',
                    reason: '',
                    requestedDays: 0,
                    documentUrl: '',
                  });
                  setFormErrors({});
                  setTouched({});
                }}
                leavePolicies={leavePolicies}
                loadingPolicies={loadingPolicies}
                selectedEmployeeData={employeeData}
                validateField={validateSingleField}
                isUploading={isUploading}
                handleFileUpload={handleFileUpload}
                handleRemoveFile={handleRemoveFile}
                isValidating={loading}
                handleTextKeyPress={(e) => {}}
              />
            </div>
          </div>
        )}
      </div>
      {/* Sandwich Rule Confirmation Modal */}
      {showSandwichConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in duration-200">
            <div className="bg-amber-50 p-6 border-b border-amber-100">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <AlertCircle className="h-7 w-7 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Sandwich Rule Impact</h3>
                  <p className="text-xs text-amber-700 font-medium uppercase tracking-wider">Policy Alert</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Based on the <span className="font-bold text-gray-900">Sandwich Rule</span>, the holidays/weekends falling between your leave dates will also be deducted.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-white border border-gray-100 rounded-xl shadow-sm text-center">
                    <div className="text-[10px] text-gray-400 uppercase font-bold mb-1">Requested</div>
                    <div className="text-xl font-black text-gray-700">{sandwichValidationResult?.requestedDays} Days</div>
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl shadow-sm text-center">
                    <div className="text-[10px] text-amber-500 uppercase font-bold mb-1">Sandwich Impact</div>
                    <div className="text-xl font-black text-amber-600">+{sandwichValidationResult?.sandwichDays} Days</div>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 flex items-center justify-between">
                  <span className="text-sm font-bold text-gray-700">Total Deduction:</span>
                  <span className="text-xl font-black text-blue-600">{sandwichValidationResult?.effectiveDays} Days</span>
                </div>

                {sandwichValidationResult?.affectedDates?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase mb-2">Affected Holidays/Weekends:</p>
                    <div className="flex flex-wrap gap-2">
                      {sandwichValidationResult.affectedDates.map((date, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 text-[10px] font-bold rounded-md border border-gray-200">
                          {new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowSandwichConfirm(false);
                  setSandwichValidationResult(null);
                }}
              >
                Change Dates
              </Button>
              <Button
                variant="primary"
                onClick={async () => {
                  setShowSandwichConfirm(false);
                  await processSubmit();
                }}
              >
                Confirm & Submit
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplyLeave;
